import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Download, Filter, Search, RefreshCw, CheckCircle, Upload, MapPin } from 'lucide-react';
import { getSnares, getCarcasses, updateSnareSyncStatus, updateCarcassSyncStatus } from '../lib/db';
import { syncService } from '../lib/sync';
import { AuthImage } from '../components/AuthImage';
import { getMyGroups } from '../lib/api/groups';

interface Snare {
  id: string;
  snareNumber: string;
  groupId: string;
  userId: string;
  latitude: number;
  longitude: number;
  material: string;
  description?: string;
  photos?: string[];
  photoUrls?: { id: string; thumbUrl: string; originalUrl: string }[];
  loggedAt: string;
  createdAt: string;
  syncStatus?: 'pending' | 'synced' | 'failed';
}

interface Carcass {
  id: string;
  carcassNumber: string;
  groupId: string;
  userId: string;
  latitude: number;
  longitude: number;
  species?: string;
  hadSnare: boolean;
  description?: string;
  photos?: string[];
  photoUrls?: { id: string; thumbUrl: string; originalUrl: string }[];
  loggedAt: string;
  createdAt: string;
  syncStatus?: 'pending' | 'synced' | 'failed';
}

interface DatabaseProps {
  onNavigateToMap?: (recordId: string, recordType: 'snare' | 'carcass') => void;
}

export function Database({ onNavigateToMap }: DatabaseProps = {}) {
  const { user } = useAuth();
  const [snares, setSnares] = useState<Snare[]>([]);
  const [carcasses, setCarcasses] = useState<Carcass[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'snares' | 'carcasses'>('all');
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncingRecords, setSyncingRecords] = useState<Set<string>>(new Set());
  const [modalPhoto, setModalPhoto] = useState<{ kind: 'url' | 'data'; src: string } | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    if (!user) return;

    setLoading(true);
    setError(null);

    try {
      // Show local (this device) records immediately
      const [localSnares, localCarcasses] = await Promise.all([getSnares(), getCarcasses()]);
      setSnares(localSnares as any);
      setCarcasses(localCarcasses as any);

      const token = localStorage.getItem('auth_token');
      if (!token || token === 'null') return;
      const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

      // Fetch ALL records for every group the user is an approved member of,
      // so the database view shows the whole group's data (all users), not just
      // records captured on this device. Essential for desktop admins.
      const groups = await getMyGroups().catch(() => []);
      const approved = groups.filter((g) => g.status === 'approved');

      const serverSnares: any[] = [];
      const serverCarcasses: any[] = [];
      await Promise.all(
        approved.map(async (g) => {
          const [sr, cr] = await Promise.all([
            fetch(`/api/snares/group/${g.id}`, { headers, credentials: 'include' }),
            fetch(`/api/carcasses/group/${g.id}`, { headers, credentials: 'include' }),
          ]);
          if (sr.ok) serverSnares.push(...(((await sr.json()).snares) || []));
          if (cr.ok) serverCarcasses.push(...(((await cr.json()).carcasses) || []));
        })
      );

      // Server records carry photo refs in `photos`; map them to `photoUrls` for
      // the thumbnail renderer and mark synced. Merge with local-only (pending) records.
      const mapServer = (r: any) => ({ ...r, photoUrls: r.photos || [], photos: undefined, syncStatus: 'synced' as const });
      const serverSnareIds = new Set(serverSnares.map((s) => s.id));
      const serverCarcassIds = new Set(serverCarcasses.map((c) => c.id));

      setSnares([...serverSnares.map(mapServer), ...localSnares.filter((s) => !serverSnareIds.has(s.id))] as any);
      setCarcasses([...serverCarcasses.map(mapServer), ...localCarcasses.filter((c) => !serverCarcassIds.has(c.id))] as any);

      // Reconcile local sync status for records that now exist on the server
      for (const s of localSnares) if (serverSnareIds.has(s.id) && s.syncStatus !== 'synced') await updateSnareSyncStatus(s.id, 'synced');
      for (const c of localCarcasses) if (serverCarcassIds.has(c.id) && c.syncStatus !== 'synced') await updateCarcassSyncStatus(c.id, 'synced');
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }

  async function handleSync() {
    setIsSyncing(true);
    try {
      const result = await syncService.syncAll();
      if (result.success) {
        await loadData(); // Reload after sync
      } else {
        setError(`Sync completed with ${result.failed} failures`);
      }
    } catch (err) {
      setError('Sync failed');
    } finally {
      setIsSyncing(false);
    }
  }

  async function handleSyncRecord(id: string, type: 'snare' | 'carcass') {
    setSyncingRecords(prev => new Set(prev).add(id));
    try {
      const token = localStorage.getItem('auth_token');
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const record = type === 'snare'
        ? snares.find(s => s.id === id)
        : carcasses.find(c => c.id === id);

      if (!record) return;

      const response = await fetch(`/api/${type === 'snare' ? 'snares' : 'carcasses'}`, {
        method: 'POST',
        headers,
        body: JSON.stringify(record),
        credentials: 'include',
      });

      if (response.ok) {
        if (type === 'snare') {
          await updateSnareSyncStatus(id, 'synced');
          setSnares(prev => prev.map(s => s.id === id ? { ...s, syncStatus: 'synced' } : s));
        } else {
          await updateCarcassSyncStatus(id, 'synced');
          setCarcasses(prev => prev.map(c => c.id === id ? { ...c, syncStatus: 'synced' } : c));
        }
      } else {
        throw new Error('Sync failed');
      }
    } catch (err) {
      console.error('Failed to sync record:', err);
      if (type === 'snare') {
        await updateSnareSyncStatus(id, 'failed');
      } else {
        await updateCarcassSyncStatus(id, 'failed');
      }
    } finally {
      setSyncingRecords(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  }

  function handleRecordClick(id: string, type: 'snare' | 'carcass') {
    if (onNavigateToMap) {
      onNavigateToMap(id, type);
    }
  }

  function exportToCSV() {
    const allRecords: any[] = [];

    if (filterType === 'all' || filterType === 'snares') {
      snares.forEach(s => {
        allRecords.push({
          type: 'Snare',
          number: s.snareNumber,
          latitude: s.latitude,
          longitude: s.longitude,
          material: s.material,
          species: '-',
          hadSnare: '-',
          description: s.description || '',
          loggedAt: s.loggedAt || s.createdAt
        });
      });
    }

    if (filterType === 'all' || filterType === 'carcasses') {
      carcasses.forEach(c => {
        allRecords.push({
          type: 'Carcass',
          number: c.carcassNumber,
          latitude: c.latitude,
          longitude: c.longitude,
          material: '-',
          species: c.species || '',
          hadSnare: c.hadSnare ? 'Yes' : 'No',
          description: c.description || '',
          loggedAt: c.loggedAt || c.createdAt
        });
      });
    }

    const headers = ['Type', 'Number', 'Latitude', 'Longitude', 'Material', 'Species', 'Had Snare', 'Description', 'Logged At'];
    const csvRows = [
      headers.join(','),
      ...allRecords.map(r => [
        r.type,
        r.number,
        r.latitude,
        r.longitude,
        r.material,
        r.species,
        r.hadSnare,
        `"${(r.description || '').replace(/"/g, '""')}"`,
        r.loggedAt
      ].join(','))
    ];

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `trapp-export-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  function renderThumb(record: { photos?: string[]; photoUrls?: { id: string; thumbUrl: string; originalUrl: string }[] }) {
    const serverThumb = record.photoUrls?.[0];
    if (serverThumb) {
      return (
        <button
          type="button"
          onClick={() => setModalPhoto({ kind: 'url', src: serverThumb.originalUrl })}
          className="block"
          title="View full-size (admins only)"
        >
          <AuthImage src={serverThumb.thumbUrl} alt="thumbnail" className="h-10 w-10 rounded object-cover" />
        </button>
      );
    }
    const localThumb = record.photos?.[0];
    if (localThumb) {
      return (
        <button
          type="button"
          onClick={() => setModalPhoto({ kind: 'data', src: localThumb })}
          className="block"
          title="View photo (not yet synced)"
        >
          <img src={localThumb} alt="thumbnail" className="h-10 w-10 rounded object-cover" />
        </button>
      );
    }
    return <span className="text-muted-foreground">—</span>;
  }

  const filteredSnares = snares.filter(s =>
    !searchTerm ||
    s.snareNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.material.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (s.description || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredCarcasses = carcasses.filter(c =>
    !searchTerm ||
    c.carcassNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (c.species || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (c.description || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const showSnares = filterType === 'all' || filterType === 'snares';
  const showCarcasses = filterType === 'all' || filterType === 'carcasses';

  return (
    <div className="h-full flex flex-col bg-background overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-border bg-white px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Database</h1>
            <p className="text-sm text-muted-foreground mt-1">
              View and export snare and carcass records
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleSync}
              disabled={isSyncing}
              className="flex items-center gap-2 px-4 py-2 bg-secondary text-secondary-foreground rounded-lg hover:opacity-90 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${isSyncing ? 'animate-spin' : ''}`} />
              {isSyncing ? 'Syncing...' : 'Sync'}
            </button>
            <button
              onClick={exportToCSV}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mt-4">
          <div className="p-4 bg-secondary rounded-lg">
            <p className="text-sm font-medium text-secondary-foreground">Snares</p>
            <p className="text-2xl font-bold mt-1">{snares.length}</p>
          </div>
          <div className="p-4 bg-secondary rounded-lg">
            <p className="text-sm font-medium text-secondary-foreground">Carcasses</p>
            <p className="text-2xl font-bold mt-1">{carcasses.length}</p>
          </div>
          <div className="p-4 bg-secondary rounded-lg">
            <p className="text-sm font-medium text-secondary-foreground">Total</p>
            <p className="text-2xl font-bold mt-1">{snares.length + carcasses.length}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-4 mt-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-input rounded-lg"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-muted-foreground" />
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value as any)}
              className="px-4 py-2 border border-input rounded-lg"
            >
              <option value="all">All</option>
              <option value="snares">Snares Only</option>
              <option value="carcasses">Carcasses Only</option>
            </select>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-scroll p-6 pb-24" style={{ WebkitOverflowScrolling: 'touch', touchAction: 'pan-y' }}>
        <div className="max-w-6xl mx-auto">
          {loading && (
            <div className="text-center py-12">
              <p className="text-muted-foreground">Loading data...</p>
            </div>
          )}

          {error && (
            <div className="p-4 bg-destructive/10 border border-destructive rounded-lg text-destructive">
              {error}
            </div>
          )}

          {!loading && !error && (
            <div className="space-y-6">
              {/* Snares Table */}
              {showSnares && filteredSnares.length > 0 && (
                <div>
                  <h2 className="text-lg font-semibold mb-3">Snares ({filteredSnares.length})</h2>
                  <div className="bg-white rounded-lg border border-border overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-secondary">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-semibold">Number</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold">Photo</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold">Material</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold">Date</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold">Status</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredSnares.map((snare) => (
                          <tr key={snare.id} className="border-t border-border hover:bg-secondary/50">
                            <td className="px-4 py-3 text-sm font-mono">{snare.snareNumber}</td>
                            <td className="px-4 py-3 text-sm">{renderThumb(snare)}</td>
                            <td className="px-4 py-3 text-sm capitalize">{snare.material}</td>
                            <td className="px-4 py-3 text-sm">
                              {new Date(snare.loggedAt || snare.createdAt).toLocaleDateString()}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              {snare.syncStatus === 'synced' ? (
                                <span className="flex items-center gap-1 text-green-600">
                                  <CheckCircle className="w-4 h-4" />
                                  <span className="text-xs">Synced</span>
                                </span>
                              ) : snare.syncStatus === 'failed' ? (
                                <span className="text-xs text-red-600">Failed</span>
                              ) : (
                                <span className="text-xs text-blue-600">Pending</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <div className="flex items-center gap-2">
                                {snare.syncStatus !== 'synced' && (
                                  <button
                                    onClick={() => handleSyncRecord(snare.id, 'snare')}
                                    disabled={syncingRecords.has(snare.id)}
                                    className="p-1 rounded hover:bg-secondary disabled:opacity-50"
                                    title="Sync to cloud"
                                  >
                                    <Upload className={`w-4 h-4 ${syncingRecords.has(snare.id) ? 'animate-pulse' : ''}`} />
                                  </button>
                                )}
                                <button
                                  onClick={() => handleRecordClick(snare.id, 'snare')}
                                  className="p-1 rounded hover:bg-secondary"
                                  title="View on map"
                                >
                                  <MapPin className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Carcasses Table */}
              {showCarcasses && filteredCarcasses.length > 0 && (
                <div>
                  <h2 className="text-lg font-semibold mb-3">Carcasses ({filteredCarcasses.length})</h2>
                  <div className="bg-white rounded-lg border border-border overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-secondary">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-semibold">Number</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold">Photo</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold">Species</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold">Snare</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold">Date</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold">Status</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredCarcasses.map((carcass) => (
                          <tr key={carcass.id} className="border-t border-border hover:bg-secondary/50">
                            <td className="px-4 py-3 text-sm font-mono">{carcass.carcassNumber}</td>
                            <td className="px-4 py-3 text-sm">{renderThumb(carcass)}</td>
                            <td className="px-4 py-3 text-sm">{carcass.species || '-'}</td>
                            <td className="px-4 py-3 text-sm">
                              {carcass.hadSnare ? (
                                <span className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs font-medium">Yes</span>
                              ) : (
                                <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded text-xs font-medium">No</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              {new Date(carcass.loggedAt || carcass.createdAt).toLocaleDateString()}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              {carcass.syncStatus === 'synced' ? (
                                <span className="flex items-center gap-1 text-green-600">
                                  <CheckCircle className="w-4 h-4" />
                                  <span className="text-xs">Synced</span>
                                </span>
                              ) : carcass.syncStatus === 'failed' ? (
                                <span className="text-xs text-red-600">Failed</span>
                              ) : (
                                <span className="text-xs text-blue-600">Pending</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <div className="flex items-center gap-2">
                                {carcass.syncStatus !== 'synced' && (
                                  <button
                                    onClick={() => handleSyncRecord(carcass.id, 'carcass')}
                                    disabled={syncingRecords.has(carcass.id)}
                                    className="p-1 rounded hover:bg-secondary disabled:opacity-50"
                                    title="Sync to cloud"
                                  >
                                    <Upload className={`w-4 h-4 ${syncingRecords.has(carcass.id) ? 'animate-pulse' : ''}`} />
                                  </button>
                                )}
                                <button
                                  onClick={() => handleRecordClick(carcass.id, 'carcass')}
                                  className="p-1 rounded hover:bg-secondary"
                                  title="View on map"
                                >
                                  <MapPin className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {!loading && !error && showSnares && filteredSnares.length === 0 && showCarcasses && filteredCarcasses.length === 0 && (
                <div className="text-center py-12">
                  <p className="text-muted-foreground">No records found</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {modalPhoto && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={() => setModalPhoto(null)}
        >
          {modalPhoto.kind === 'url' ? (
            <AuthImage
              src={modalPhoto.src}
              alt="Full-size photo"
              className="max-h-full max-w-full rounded-lg"
              fallback={
                <div className="rounded-lg bg-white px-6 py-4 text-center text-sm text-muted-foreground">
                  Full-size photos are restricted to group admins.
                </div>
              }
            />
          ) : (
            <img src={modalPhoto.src} alt="Full-size photo" className="max-h-full max-w-full rounded-lg" />
          )}
        </div>
      )}
    </div>
  );
}
